/**
 * TopNav – global app header (brand, main tabs, header actions).
 * Single source of truth for the top navigation; styles live in index.css under .top-nav.
 * Shows user initials when authenticated (click opens Account); Login button when not.
 */
import { useAuth } from '../contexts/AuthContext.jsx'

function getInitials(user) {
  if (!user) return null
  const first = (user.first_name || '').trim()
  const last = (user.last_name || '').trim()
  if (first && last) return (first[0] + last[0]).toUpperCase()
  if (first) return first.slice(0, 2).toUpperCase()
  if (last) return last.slice(0, 2).toUpperCase()
  const email = (user.email || '').trim()
  if (email) return email.slice(0, 2).toUpperCase()
  return '?'
}

export default function TopNav({
  activeTab,
  onTabChange,
  onLoginClick,
  chatOpen = false,
  askLauncherOpen = false,
  onAskLauncherToggle,
  askNavAnchorRef,
  todayCookTarget = null,
  onTodayCookClick,
  navigationLocked = false,
  navigationLockReason = 'Start your kitchen or log in to continue.',
  /** Setup steps: tabs stay locked, without lock icons or the active-tab fill. */
  navigationQuiet = false,
  hideMainNavigation = false,
  hideAsk = false,
  hideLogin = false,
  onBrandClick = null,
}) {
  const { isAuthenticated, user } = useAuth()
  const initials = isAuthenticated && user ? getInitials(user) : null
  const lockedClass = navigationLocked
    ? ` is-locked${navigationQuiet ? ' is-quiet' : ''}`
    : ''
  const showLockIcon = navigationLocked && !navigationQuiet

  return (
    <header className="top-nav" id="navigation">
      <div className={`nav-inner${hideMainNavigation ? ' nav-inner--focused' : ''}`}>
        {onBrandClick ? (
          <button type="button" className="brand" onClick={onBrandClick} aria-label="Captain Cook, home">
            <img src="/logo.svg" alt="" className="brand-logo" aria-hidden="true" />
            <span className="brand-title-stacked" aria-hidden="true">
              <span>Captain</span>
              <span>Cook</span>
            </span>
          </button>
        ) : (
          <div className="brand">
            <img src="/logo.svg" alt="" className="brand-logo" aria-hidden="true" />
            <span className="brand-title-stacked" aria-hidden="true">
              <span>Captain</span>
              <span>Cook</span>
            </span>
            <span className="sr-only">Captain Cook</span>
          </div>
        )}
        {!hideMainNavigation ? (
        <nav className="tab-nav desktop-tabs" role="tablist" aria-label="Main navigation">
          <button
            className={`tab-button ${activeTab === 'plan' ? 'active' : ''}${lockedClass}`}
            onClick={navigationLocked ? undefined : () => onTabChange('plan')}
            role="tab"
            aria-selected={activeTab === 'plan'}
            aria-controls="plan-panel"
            aria-label={navigationLocked ? `Plan. ${navigationLockReason}` : 'Plan tab'}
            aria-disabled={navigationLocked || undefined}
            data-testid="main-tab-plan"
          >
            <span className="tab-label" data-text="Plan">Plan</span>
            {showLockIcon ? <i className="fas fa-lock tab-lock" aria-hidden="true" /> : null}
          </button>
          <button
            className={`tab-button ${activeTab === 'recipes' ? 'active' : ''}${lockedClass}`}
            onClick={navigationLocked ? undefined : () => onTabChange('recipes')}
            role="tab"
            aria-selected={activeTab === 'recipes'}
            aria-controls="recipes-panel"
            aria-label={navigationLocked ? `Recipes. ${navigationLockReason}` : 'Recipes tab'}
            aria-disabled={navigationLocked || undefined}
            data-testid="main-tab-recipes"
          >
            <span className="tab-label" data-text="Recipes">Recipes</span>
            {showLockIcon ? <i className="fas fa-lock tab-lock" aria-hidden="true" /> : null}
          </button>
          <button
            className={`tab-button ${activeTab === 'pantry' ? 'active' : ''}${lockedClass}`}
            onClick={navigationLocked ? undefined : () => onTabChange('pantry')}
            role="tab"
            aria-selected={activeTab === 'pantry'}
            aria-controls="pantry-panel"
            aria-label={navigationLocked ? `Pantry. ${navigationLockReason}` : 'Pantry tab'}
            aria-disabled={navigationLocked || undefined}
            data-testid="main-tab-pantry"
          >
            <span className="tab-label" data-text="Pantry">Pantry</span>
            {showLockIcon ? <i className="fas fa-lock tab-lock" aria-hidden="true" /> : null}
          </button>
          <button
            className={`tab-button ${activeTab === 'shop' ? 'active' : ''}${lockedClass}`}
            onClick={navigationLocked ? undefined : () => onTabChange('shop')}
            role="tab"
            aria-selected={activeTab === 'shop'}
            aria-controls="shop-panel"
            aria-label={navigationLocked ? `Shop. ${navigationLockReason}` : 'Shop tab'}
            aria-disabled={navigationLocked || undefined}
            data-testid="main-tab-shop"
          >
            <span className="tab-label" data-text="Shop">Shop</span>
            {showLockIcon ? <i className="fas fa-lock tab-lock" aria-hidden="true" /> : null}
          </button>
        </nav>
        ) : null}
        <div className="header-actions">
          {todayCookTarget?.recipeId && typeof onTodayCookClick === 'function' && (
            <button
              type="button"
              className="nav-today-cook-btn"
              onClick={onTodayCookClick}
              aria-label={`Start Cook Mode for ${todayCookTarget.recipeName}`}
              title={`Cook ${todayCookTarget.recipeName}`}
              data-testid="nav-today-cook"
            >
              Start Cooking
            </button>
          )}
          {!hideAsk && typeof onAskLauncherToggle === 'function' && (
            <button
              ref={askNavAnchorRef}
              type="button"
              className={`nav-ask-field${chatOpen || askLauncherOpen ? ' active' : ''}${!isAuthenticated ? ' is-locked' : ''}`}
              onClick={onAskLauncherToggle}
              aria-label={!isAuthenticated ? 'Ask the Cook. Create an account to get help from the Captain.' : chatOpen ? 'Close Ask the Cook' : 'Ask the Cook'}
              aria-disabled={!isAuthenticated || undefined}
              aria-expanded={chatOpen || askLauncherOpen}
              aria-haspopup="dialog"
              data-testid="nav-chat-toggle"
            >
              <span className="nav-ask-sparkle" aria-hidden="true">✦</span>
              <span className="nav-ask-placeholder">Ask the Cook — what can I make with…</span>
              <span className="nav-ask-hint" aria-hidden="true">⌘/</span>
              {!isAuthenticated ? <i className="fas fa-lock nav-ask-lock" aria-hidden="true" /> : null}
            </button>
          )}
          {initials ? (
            <button
              type="button"
              className="nav-user-initials"
              title={`Logged in${user?.email ? ` as ${user.email}` : ''}. Click to open account.`}
              aria-label="Open account"
              onClick={() => onTabChange('account', 'preferences')}
            >
              {initials}
            </button>
          ) : hideLogin ? null : (
            <button
              type="button"
              className={navigationQuiet ? 'nav-login-link' : 'nav-login-btn'}
              aria-label="Log in"
              onClick={onLoginClick}
            >
              Login
            </button>
          )}
        </div>
      </div>
    </header>
  )
}
